import { Link } from 'react-router-dom';
import { featureCards } from '@/mocks/categories';

export default function SeoContent() {
  return (
    <>
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-3 whitespace-nowrap">
                What Is It?
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                What Is <span className="text-primary-500">Sugargoo Spreadsheet</span>?
              </h2>
              <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                <p>
                  <strong className="text-foreground-800">Sugargoo Spreadsheet</strong> is a powerful way to discover organized product collections, shopping recommendations and curated finds from the Sugargoo community. Instead of searching through thousands of random links across multiple platforms, users can explore categorized resources and make faster, smarter purchasing decisions.
                </p>
                <p>
                  These spreadsheets are created and maintained by experienced community members who gather trending items, quality-tested products and hidden gems across Chinese marketplaces. Whether you are looking for the latest sneaker releases, designer-inspired fashion, streetwear essentials or unique accessories, a well-organized <strong className="text-foreground-800">Sugargoo Spreadsheet</strong> saves hours of research time.
                </p>
                <p>
                  Each spreadsheet entry typically includes product links, size recommendations, quality ratings, price comparisons and community feedback — giving you comprehensive information before you buy. The <strong className="text-foreground-800">Sugargoo Spreadsheet</strong> ecosystem has grown into one of the most valuable resources for international shoppers navigating Chinese e-commerce platforms.
                </p>
              </div>
              <div className="mt-6">
                <Link
                  to="/article/sugargoo-spreadsheet-complete-guide"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  Learn More About Sugargoo Spreadsheet
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimal%20workspace%20with%20macbook%20displaying%20colorful%20organized%20spreadsheet%20data%2C%20warm%20cream%20desk%20with%20small%20plant%2C%20soft%20natural%20daylight%20from%20side%20window%2C%20cozy%20productivity%20aesthetic%2C%20neutral%20warm%20tones%2C%20clean%20editorial%20photography&width=800&height=600&seq=what-is-v2&orientation=landscape"
                  alt="Organized Sugargoo Spreadsheet on modern workspace"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-background-50 rounded-xl px-5 py-3 border border-background-200/70">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-secondary-500"></div>
                  <span className="text-xs font-semibold text-foreground-700 whitespace-nowrap">Updated Weekly</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-3 whitespace-nowrap">
              Features
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              Everything You Need for <span className="text-primary-500">Sugargoo Spreadsheet</span> Success
            </h2>
            <p className="text-sm text-foreground-500 mt-3 max-w-xl mx-auto">
              Our independent resource hub covers every aspect of the Sugargoo shopping experience, from product discovery to smart shipping strategies.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureCards.map((card) => (
              <div key={card.seq} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300">
                <div className="w-full aspect-[16/10] overflow-hidden">
                  <img
                    src={card.imageUrl}
                    alt={card.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <i className={`${card.icon} text-primary-500 text-lg`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-base text-foreground-800">
                      {card.title}
                    </h3>
                  </div>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              <span className="text-primary-500">Sugargoo Spreadsheet</span> vs Other Methods
            </h2>
            <p className="text-sm text-foreground-500 mt-3 max-w-lg mx-auto">
              See why organized Sugargoo Spreadsheets outperform random searching and uncurated product links.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full max-w-3xl mx-auto bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <thead>
                <tr className="border-b border-background-200/70">
                  <th className="text-left px-6 py-4 text-sm font-semibold text-foreground-700 whitespace-nowrap">Feature</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-primary-600 whitespace-nowrap bg-primary-50/50">
                    <i className="ri-star-fill text-xs mr-1"></i>
                    Sugargoo Spreadsheet
                  </th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-foreground-500 whitespace-nowrap">Normal Search</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-foreground-500 whitespace-nowrap">Random Links</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Organization', ss: true, ns: false, rl: false },
                  { feature: 'Time Saving', ss: true, ns: false, rl: false },
                  { feature: 'Category Filtering', ss: true, ns: true, rl: false },
                  { feature: 'Shopping Research', ss: true, ns: false, rl: false },
                  { feature: 'Beginner Friendly', ss: true, ns: true, rl: false },
                  { feature: 'Quality Ratings', ss: true, ns: false, rl: false },
                  { feature: 'Community Verified', ss: true, ns: false, rl: false },
                ].map((row) => (
                  <tr key={row.feature} className="border-b border-background-200/70 last:border-0">
                    <td className="px-6 py-3.5 text-sm font-medium text-foreground-700 whitespace-nowrap">{row.feature}</td>
                    <td className="px-6 py-3.5 text-center bg-primary-50/50">
                      <i className="ri-checkbox-circle-fill text-primary-500 text-lg"></i>
                    </td>
                    <td className="px-6 py-3.5 text-center">
                      {row.ns ? (
                        <i className="ri-checkbox-circle-fill text-secondary-500 text-lg"></i>
                      ) : (
                        <i className="ri-close-circle-fill text-foreground-300 text-lg"></i>
                      )}
                    </td>
                    <td className="px-6 py-3.5 text-center">
                      {row.rl ? (
                        <i className="ri-checkbox-circle-fill text-secondary-500 text-lg"></i>
                      ) : (
                        <i className="ri-close-circle-fill text-foreground-300 text-lg"></i>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}